package com.ijoin.ui.components

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Favorite
import androidx.compose.material.icons.outlined.FavoriteBorder
import androidx.compose.material.icons.outlined.Image
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import coil3.compose.AsyncImage
import com.ijoin.data.api.ApiClient
import com.ijoin.ui.util.toThumbUrl
import com.ijoin.data.model.Event
import com.ijoin.i18n.strings
import com.ijoin.platform.createTimeZone
import com.ijoin.ui.design.EventCardAccentDark
import com.ijoin.ui.design.EventCardAccentSoft
import com.ijoin.ui.design.EventCardAlert
import com.ijoin.ui.design.EventCardCompactCorner
import com.ijoin.ui.design.EventCardDateBadgeBackground
import com.ijoin.ui.design.EventCardDateBadgeText
import com.ijoin.ui.design.EventCardOutline
import com.ijoin.ui.design.EventCardPriceFree
import com.ijoin.ui.design.EventCardPricePaid
import com.ijoin.ui.design.EventCardSurface
import com.ijoin.ui.design.EventPill
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

@Composable
fun EventCard(
    event: Event,
    isFavorite: Boolean,
    onEventClick: (String) -> Unit,
    onFavoriteClick: (String) -> Unit,
    modifier: Modifier = Modifier
) {
    val strings = strings()

    Card(
        modifier = modifier
            .width(160.dp)
            .clickable { onEventClick(event.id) },
        shape = RoundedCornerShape(EventCardCompactCorner),
        colors = CardDefaults.cardColors(containerColor = EventCardSurface),
        border = BorderStroke(1.dp, EventCardOutline),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
    ) {
        Column {
            // Image with date and favorite overlay
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(140.dp)
            ) {
                // Event image
                if (!event.mediaUrls.isNullOrEmpty()) {
                    val first = event.mediaUrls.first()
                    val thumbPath = if (first.startsWith("http")) first else toThumbUrl(first, 300)
                    val imageUrl = if (thumbPath.startsWith("http")) thumbPath else "${ApiClient.getBaseUrl()}$thumbPath"
                    AsyncImage(
                        model = imageUrl,
                        contentDescription = event.title,
                        modifier = Modifier.fillMaxSize(),
                        contentScale = ContentScale.Crop
                    )
                } else {
                    // Placeholder
                    Box(
                        modifier = Modifier
                            .fillMaxSize()
                            .background(EventCardAccentSoft),
                        contentAlignment = Alignment.Center
                    ) {
                        Icon(
                            imageVector = Icons.Outlined.Image,
                            contentDescription = null,
                            modifier = Modifier.size(48.dp),
                            tint = EventCardAccentDark.copy(alpha = 0.4f)
                        )
                    }
                }

                // Date badge (top left)
                val badgeText = formatEventDateShort(event.eventDate, event.eventEndDate, event.timeZone)
                if (badgeText != null) {
                    EventPill(
                        text = badgeText,
                        backgroundColor = EventCardDateBadgeBackground,
                        textColor = EventCardDateBadgeText,
                        borderColor = EventCardOutline,
                        textStyle = MaterialTheme.typography.labelSmall,
                        fontWeight = FontWeight.Bold,
                        contentPadding = PaddingValues(horizontal = 8.dp, vertical = 4.dp),
                        modifier = Modifier
                            .align(Alignment.TopStart)
                            .padding(8.dp)
                    )
                }

                // Favorite icon (top right)
                IconButton(
                    onClick = { onFavoriteClick(event.id) },
                    modifier = Modifier
                        .align(Alignment.TopEnd)
                        .padding(4.dp)
                        .size(32.dp)
                ) {
                    Icon(
                        imageVector = if (isFavorite) Icons.Filled.Favorite else Icons.Outlined.FavoriteBorder,
                        contentDescription = if (isFavorite) "Remove from favorites" else "Add to favorites",
                        tint = if (isFavorite) EventCardAlert else EventCardSurface,
                        modifier = Modifier.size(20.dp)
                    )
                }
            }

            // Event info
            Column(
                modifier = Modifier.padding(12.dp),
                verticalArrangement = Arrangement.spacedBy(4.dp)
            ) {
                // Event title
                Text(
                    text = event.title,
                    style = MaterialTheme.typography.titleSmall,
                    fontWeight = FontWeight.Bold,
                    color = EventCardAccentDark,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis,
                    modifier = Modifier.height(40.dp)
                )

                // Location
                event.locationCity?.let { city ->
                    Text(
                        text = city,
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                }

                // Price
                event.priceTiers?.minByOrNull { it.price }?.let { minTier ->
                    Text(
                        text = formatPrice(minTier.price),
                        style = MaterialTheme.typography.labelLarge,
                        fontWeight = FontWeight.Bold,
                        color = if (minTier.price == 0.0) EventCardPriceFree else EventCardPricePaid
                    )
                }
            }
        }
    }
}

private fun formatEventDateShort(start: Instant?, end: Instant?, timeZone: String? = null): String? {
    val startValue = start ?: return null
    val zone = if (!timeZone.isNullOrBlank()) createTimeZone(timeZone) else TimeZone.UTC
    val startDate = startValue.toLocalDateTime(zone).date
    val startLabel = toMonthDay(startDate)
    val endDate = end?.toLocalDateTime(zone)?.date
    val endLabel = when {
        endDate == null -> null
        endDate == startDate -> null
        else -> toMonthDay(endDate)
    }
    return if (endLabel == null) startLabel else "$startLabel – $endLabel"
}

private fun toMonthDay(date: kotlinx.datetime.LocalDate): String {
    return try {
        val month = getMonthAbbr(date.monthNumber)
        val day = date.dayOfMonth
        "$month $day"
    } catch (e: Exception) {
        date.toString()
    }
}

private fun formatPrice(price: Double): String {
    return if (price == 0.0) "Free" else "$$${price.toInt()}"
}

private fun getMonthAbbr(month: Int): String {
    return when (month) {
        1 -> "Jan"
        2 -> "Feb"
        3 -> "Mar"
        4 -> "Apr"
        5 -> "May"
        6 -> "Jun"
        7 -> "Jul"
        8 -> "Aug"
        9 -> "Sep"
        10 -> "Oct"
        11 -> "Nov"
        12 -> "Dec"
        else -> ""
    }
}
